import { useCallback, useMemo } from 'react';

import styles from './style.module.css';

// A row of outlined segments, filled up to the current step. The counter text
// beside it carries the same information for anyone who cannot see the bar, so
// the bar itself is hidden from assistive technology.
//
// Given its steps, as the daily record does (#148), each segment is a button
// carrying its step's icons (one, or one per question a step asks, joined by
// a dash: sleep and weight share a step) and, for assistive technology, its
// name (as text, not a label, so it never names the step's own fields), the
// current one marked. Pressing one asks onStep to go there; what going means,
// saving first included, is the parent's to decide. While busy, as while that
// save is under way (#158), every step is disabled.
export function StepProgress({ step, total, steps, label, busy, onStep }) {
  const plain = Array.isArray(steps) === false;

  // One handler for every step: each button carries its step's name.
  const onClick = useCallback(function({ currentTarget }) {
    onStep(currentTarget.value);
  }, [ onStep ]);

  const segments = useMemo(function() {
    if (plain === false) {
      return [];
    }

    return Array.from({ length: total }, function(_, index) {
      let className = styles.segment;

      if (index < step) {
        className = `${ styles.segment } ${ styles.done }`;
      }

      return <span key={ index } className={ className }></span>;
    });
  }, [ plain, total, step ]);

  const buttons = useMemo(function() {
    if (plain === true) {
      return [];
    }

    return steps.map(function({ name, label: stepLabel, icons = [ name ] }, index) {
      const classNames = [ styles.segment, styles.step ];
      let current = null;

      if (index < step) {
        classNames.push(styles.done);
      }

      if (index === step - 1) {
        classNames.push(styles.current);
        current = 'step';
      }

      return (
        <button key={ name } className={ classNames.join(' ') } type='button' value={ name } aria-current={ current } disabled={ busy === true } onClick={ onClick }>
          {
            icons.flatMap(function(icon, position) {
              const drawn = <span key={ icon } className={ `${ styles.icon } ${ styles[ icon ] }` } aria-hidden='true'></span>;

              if (position === 0) {
                return [ drawn ];
              }

              return [ <span key={ `${ icon }-dash` } className={ styles.dash } aria-hidden='true'></span>, drawn ];
            })
          }

          <span className={ styles.name }>{ stepLabel }</span>
        </button>
      );
    });
  }, [ plain, steps, step, busy, onClick ]);

  if (plain === true) {
    return <div className={ styles.progress } aria-hidden='true'>{ segments }</div>;
  }

  return <div className={ `${ styles.progress } ${ styles.steps }` } role='group' aria-label={ label }>{ buttons }</div>;
}
